import { useMemo, useCallback, useEffect, useState } from 'react';
import { Col, Row, Form, Badge } from 'antd';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Button } from 'components';
import { EditIcon } from 'icons';
import { useTranslation } from 'react-i18next';
import { DownloadOutlined } from '@ant-design/icons';
import { useApi } from 'hooks/useApi';
import { useRecoilValue } from 'recoil';
import { getShoppingCenters } from 'hooks/useUser';
import { useNavigate } from 'hooks/useNavigate';
import CompanyDocumentsAdapter from './invoiceMonthlies.adapter';
import UIHelper from 'helpers/UIHelper';
import ErrorHelper from 'helpers/ErrorHelper';
import Helpers from 'helpers';
import moment from 'moment';
import { useFilter } from 'hooks/useFilter';
import InvoiceMonthlies from 'libs/api/invoiceMonthlies';
import { getPage } from 'hooks/useUser';

// const data = await api.confirm_replace(companyId);

const api = new InvoiceMonthlies();
export const statusColors = {
  created: 'yellow',
  confirm: 'green',
  reject: 'red',
};

const useInvoiceMonthlies = (isMainPage = false) => {
  const { t } = useTranslation();
  const { ability } = useRecoilValue(getPage);
  const [isShowedPopup, setIsShowedPopup] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [company, setCompany] = useState(null);
  const { pathname } = useLocation();
  const [form] = Form.useForm();
  const { id } = useParams();
  const navigate = useNavigate();
  const shoppingCenters = useRecoilValue(getShoppingCenters);
  const { filterData } = useFilter();
  const additionalSingleParams = useMemo(() => {
    return { group_data: true };
  }, []);
  const {
    singleItem,
    loading,
    data,
    onChangeTableFilter,
    updateInProcess,
    setUpdateInProcess,
    approveErrors,
    setApproveErrors,
  } = useApi({
    resourceKey: 'invoice_monthlies',
    singleResourceKey: 'invoice_monthly',
    api: api,
    isMainPage,
    adapter: CompanyDocumentsAdapter,
    aditionalParamsForSingle: additionalSingleParams,
  });

  const statusTexts = useMemo(
    () => ({
      confirm: t('Confermato'),
      created: t('Nuovo'),
      reject: t('Rifiutato'),
    }),
    [],
  );

  const handleCreate = useCallback(
    async (values, step) => {
      setUpdateInProcess(true);
      const formData = new FormData();

      if (values.month && values.year) {
        const period = moment(`${values.year}-${values.month}-01`).format(
          'YYYY-MM-DD',
        );
        formData.append('period', period);
      }

      Object.keys(values).forEach((key) => {
        if (['file', 'month', 'year'].includes(key)) {
          if (key === 'file' && values[key]) {
            formData.append(`${key}`, values[key][0]?.originFileObj || '');
          }
        } else {
          formData.append(`${key}`, values[key]);
        }
      });

      try {
        if (step === 'stepOne') {
          const { data } = await api.create({
            data: formData,
            headers: { 'Content-Type': 'multipart/Form-data' },
          });
          setCompany(data);
          if (data?.confirmation_for_replace_needed) {
            setShowModal(true);
            return setIsShowedPopup(true);
          }
          return navigate(UIHelper.getListRoute(pathname, 1));
        } else if (step === 'stepTwo') {
          const data = await api.confirm_replace(company?.invoice_monthly?.id);
          if (!data || data?.errors?.length) {
            UIHelper.openNotificationWithIcon(
              'error',
              'Something went wrong, try again',
            );
            return false;
          }
          navigate(UIHelper.getListRoute(pathname, 1));
          return data.data.success;
        } else {
          return navigate(UIHelper.getListRoute(pathname, 1));
        }
      } catch (e) {
        const errors = e.response.data?.errors || [];
        UIHelper.openNotificationWithIcon(
          'error',
          e?.response?.data?.errors[0]?.messages[0],
        );
        form.setFields(
          ErrorHelper.getErrorsForForm(errors[0], {
            company: 'company_id',
          }),
        );
      } finally {
        setUpdateInProcess(false);
      }
    },
    [setUpdateInProcess, form, navigate, company, pathname, shoppingCenters],
  );

  const handleCreateOrUpdate = useCallback(
    async (values) => {
      setUpdateInProcess(true);
      const formData = new FormData();
      Object.keys(values).forEach((key) => {
        if (key === 'file') {
          if (values[key]) {
            formData.append(`${key}`, values[key][0]?.originFileObj || '');
          }
        } else if (key === 'period') {
          formData.append(key, moment(values[key]).format('YYYY-MM-DD'));
        } else {
          formData.append(`${key}`, values[key]);
        }
      });
      try {
        if (!id) {
          await api.create({
            data: formData,
            headers: { 'Content-Type': 'multipart/Form-data' },
          });
          navigate(UIHelper.getListRoute(pathname, 1));
        } else {
          await api.put(id, {
            data: formData,
            headers: { 'Content-Type': 'multipart/Form-data' },
          });
          navigate(UIHelper.getListRoute(pathname));
        }
      } catch (e) {
        const errors = e.response.data?.errors || [];

        form.setFields(
          ErrorHelper.getErrorsForForm(errors[0], {
            company: 'company_id',
          }),
        );
      } finally {
        setUpdateInProcess(false);
      }
    },
    [setUpdateInProcess, form, navigate, pathname, id, shoppingCenters],
  );

  const onClickFile = useCallback(async (filePath) => {
    const fullPath = Helpers.getFilePath(filePath);
    window.open(fullPath, '_blank');
  }, []);

  const columns = useMemo(() => {
    return [
      {
        title: t('Insegna'),
        dataIndex: 'companies.title',
        sorter: () => {},
      },
      {
        title: t('Mese e anno del fatturato'),
        dataIndex: 'periodNormal',
        sorter: () => {},
        render: (value) => moment(value).format('MM/YYYY'),
      },
      {
        title: t('Data caricamento'),
        dataIndex: 'file',
        sorter: () => {},
        render: (file) =>
          file && file.created_at
            ? moment(file.created_at).format('DD/MM/YYYY')
            : '',
      },
      {
        title: t('Stato'),
        dataIndex: 'status',
        sorter: () => {},
        render: (data) => (
          <Badge color={statusColors[data]} text={statusTexts[data]} />
        ),
      },
      {
        title: t('Scarica file'),
        dataIndex: 'file',
        render: (data) =>
          data && (
            <Button
              htmlFor='download-file'
              type='primary-white'
              icon={<DownloadOutlined style={{ marginRight: '10px' }} />}
            >
              <a
                id='download-file'
                target='_blank'
                rel='noopener noreferrer'
                href={data?.url}
              >
                {t('Scarica File')}
              </a>
            </Button>
          ),
      },
      {
        title: '',
        dataIndex: 'address',
        align: 'right',
        width: 100,
        fixed: 'right',
        render: (data, row) => {
          return (
            <Row
              gutter={16}
              justify={'center'}
              onClick={(e) => e.stopPropagation()}
            >
              {row?.can_update !== false && ability?.can_update && (
                <Col>
                  <Link to={`${pathname}/${row.id}/edit`}>
                    <Button type={'secondary'} icon={<EditIcon />} />
                  </Link>
                </Col>
              )}
              {/*{row.can_destroy !== false && (*/}
              {/*  <Col>*/}
              {/*    <Link to={`${pathname}/${row.id}/delete`}>*/}
              {/*      <Button type={'red'} icon={<DeleteIcon />} />*/}
              {/*    </Link>*/}
              {/*  </Col>*/}
              {/*)}*/}
            </Row>
          );
        },
      },
    ];
  }, [pathname, t, onClickFile, ability, statusTexts]);

  useEffect(() => {
    if (id && !isMainPage && singleItem?.id) {
      form.setFields([
        {
          name: 'period',
          value:
            singleItem.file &&
            moment(singleItem.file.created_at).format('DD/MM/YYYY HH:mm'),
        },
        {
          name: 'file',
          value: null,
        },
        {
          name: 'company_id',
          value: singleItem.company?.id,
        },
      ]);
    }
  }, [singleItem, form, id, isMainPage]);

  const handleApprove = async (values, paramKey, backCount) => {
    const data = {
      comment: values.description,
      company_id: singleItem?.company?.id,
      by_month: singleItem?.period,
    };
    switch (values.status) {
      case 'Approva': {
        data.status = 'confirm';
        break;
      }
      case 'Rifiutato': {
        data.status = 'reject';
        break;
      }
      default:
        break;
    }
    setUpdateInProcess(true);
    try {
      await api.approve(id, data);
      navigate(UIHelper.getListRoute(pathname, backCount));
    } catch (e) {
      setApproveErrors(
        (e?.response?.data?.errors &&
          e?.response?.data?.errors[0] &&
          e?.response?.data?.errors[0]?.messages) ||
          [],
      );
    } finally {
      setUpdateInProcess(false);
    }
  };

  const handleDownloadPdf = useCallback(
    async (url, fileName = 'vacancy_application.pdf') => {
      setUpdateInProcess(true);
      try {
        const response = await api.getPDF(url);
        Helpers.downloadFile(response, fileName);
      } catch (e) {
        UIHelper.serverError();
      } finally {
        setUpdateInProcess(false);
      }
    },
    [],
  );

  const handleDownloadExcel = async () => {
    try {
      const response = await api.getExcelSingle(id);
      Helpers.downloadFile(response, 'invoice.xlsx');
    } catch (e) {
      const error = e?.response?.data?.errors || [t('Something went wrong!')];
      UIHelper.openNotificationWithIcon('error', error.join('\n'));
    }
  };

  const filters = useMemo(
    () => [
      {
        title: t('Insegna'),
        type: 'select',
        name: 'company_id',
        values: filterData?.companies?.map((i) => ({
          value: i.value,
          title: i.title,
        })),
        inactiveValues: filterData?.inactive_companies?.map((i) => ({
          value: i.value,
          title: i.title,
        })),
      },
      {
        title: t('Village'),
        type: 'select',
        name: 'shopping_center_id',
        values: filterData?.shopping_centers?.map((i) => ({
          value: i.value,
          title: i.title,
        })),
      },
      {
        title: t('Date'),
        type: 'range',
        picker: 'month',
        name: 'by_month',
        keys: ['from', 'to'],
      },
      {
        title: t('Stato'),
        type: 'select',
        name: 'by_status',
        values: Object.entries(statusTexts)?.map(([key, value]) => ({
          value: key,
          title: value,
        })),
      },
    ],
    [t, shoppingCenters, filterData, statusTexts],
  );

  return {
    columns,
    singleItem,
    form,
    data,
    loading,
    filters,
    updateInProcess,
    onChangeTableFilter,
    handleCreateOrUpdate,
    handleCreate,
    shops: filterData?.companies,
    inactiveShops: filterData?.inactive_companies || [],
    handleApprove,
    approveErrors,
    handleDownloadPdf,
    handleDownloadExcel,
    statusColors,
    statusTexts,
    isShowedPopup,
    setIsShowedPopup,
    showModal,
    setShowModal,
  };
};

export default useInvoiceMonthlies;
